import { useState, useCallback, useEffect, useMemo, useRef } from 'react'

import { WordHuntService } from '../services/gameService'
import type {
  GameBoard,
  GameConfig,
  GamePosition,
  GameState,
  GameActions
} from '../types'

/**
 * Hook personalizado para gerenciar o estado do jogo WordHunt
 * Segue princípios de Clean Code: responsabilidade única, imutabilidade
 */
// Chave para armazenar o estado do jogo no sessionStorage
const STORAGE_KEY = 'wordhunt-game-state'

// Persistência do tabuleiro entre montagens via sessionStorage.
// Mantida DESATIVADA por padrão: restaurar um tabuleiro antigo sem restaurar
// também `foundWords` gera incoerência de progresso. Toggle explícito (substitui
// o antigo `if (1 == 1) return`) para tornar a intenção clara e reversível.
const PERSIST_GAME_STATE = false

// Interface para o estado persistido
interface PersistedGameState {
  gameBoard: GameBoard
  config: GameConfig
  timestamp: number
}

export function useWordHunt(config: GameConfig, actions: GameActions) {
  // Estado do jogo
  const [gameState, setGameState] = useState<GameState>({
    currentWord: '',
    selectedPositions: [],
    foundWords: [],
    isSelecting: false
  })

  // Estado para indicar erro temporário na seleção
  const [selectionError, setSelectionError] = useState(false)

  // Estado para indicar sucesso temporário na seleção
  const [selectionSuccess, setSelectionSuccess] = useState(false)

  // Verificar se a configuração mudou significativamente
  const configKey = useMemo(() => {
    return `${config.words.join(',')}-${config.boardSize}-${config.showWords}`
  }, [config.words, config.boardSize, config.showWords])

  // Função para salvar estado no sessionStorage
  const saveGameState = useCallback(
    (board: GameBoard, gameConfig: GameConfig) => {
      if (!PERSIST_GAME_STATE) return
      try {
        const stateToSave: PersistedGameState = {
          gameBoard: board,
          config: gameConfig,
          timestamp: Date.now()
        }
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave))
      } catch (error) {
        console.warn('Não foi possível salvar o estado do jogo:', error)
      }
    },
    []
  )

  // Função para limpar estado salvo
  const clearSavedState = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY)
    } catch (error) {
      console.warn('Erro ao limpar estado salvo:', error)
    }
  }, [])

  // Função para carregar estado do sessionStorage
  const loadGameState = useCallback((): {
    board: GameBoard | null
    config: GameConfig | null
  } => {
    try {
      if (!PERSIST_GAME_STATE) return { board: null, config: null }
      const savedState = sessionStorage.getItem(STORAGE_KEY)
      if (!savedState) return { board: null, config: null }

      const parsedState: PersistedGameState = JSON.parse(savedState)

      // Verificar se o estado ainda é válido (não tem mais de 24 horas)
      const isExpired = Date.now() - parsedState.timestamp > 24 * 60 * 60 * 1000
      if (isExpired) {
        clearSavedState()
        return { board: null, config: null }
      }

      // Verificar se a configuração mudou
      const savedConfigKey = `${parsedState.config.words.join(',')}-${
        parsedState.config.boardSize
      }-${parsedState.config.showWords}`
      if (savedConfigKey !== configKey) {
        clearSavedState()
        return { board: null, config: null }
      }

      // Validar estrutura do estado salvo
      if (!parsedState.gameBoard?.board || !parsedState.config?.words) {
        console.warn('⚠️ Estado salvo inválido, limpando...')
        clearSavedState()
        return { board: null, config: null }
      }

      return { board: parsedState.gameBoard, config: parsedState.config }
    } catch (error) {
      console.warn('❌ Erro ao carregar estado do jogo, limpando:', error)
      clearSavedState()
      return { board: null, config: null }
    }
  }, [configKey, clearSavedState])

  // Gera o tabuleiro apenas quando necessário
  const gameBoard = useMemo(() => {
    // Primeiro, tentar carregar do sessionStorage
    const { board: savedBoard } = loadGameState()
    if (savedBoard) {
      return savedBoard
    }

    // Se não tem estado salvo, gerar novo tabuleiro
    const newBoard = WordHuntService.generateBoard(config)

    // Salvar no sessionStorage para persistência
    saveGameState(newBoard, config)

    return newBoard
  }, [configKey, config, loadGameState, saveGameState])

  // Encontra todas as posições das letras que fazem parte de palavras encontradas
  const foundLetterPositions = useMemo(() => {
    const positions = new Set<string>()

    gameState.foundWords.forEach(word => {
      // Para cada palavra encontrada, encontrar todas as posições possíveis
      // que podem formar essa palavra no tabuleiro
      for (let row = 0; row < gameBoard.board.length; row++) {
        for (let col = 0; col < gameBoard.board[row].length; col++) {
          if (gameBoard.board[row][col].letter === word[0]) {
            // Verificar todas as direções possíveis
            const directions = [
              [0, 1], // horizontal direita
              [0, -1], // horizontal esquerda
              [1, 0], // vertical baixo
              [-1, 0], // vertical cima
              [1, 1], // diagonal baixo-direita
              [1, -1], // diagonal baixo-esquerda
              [-1, 1], // diagonal cima-direita
              [-1, -1] // diagonal cima-esquerda
            ]

            directions.forEach(([deltaRow, deltaCol]) => {
              let isValidWord = true
              const currentPositions: GamePosition[] = []

              // Verificar se toda a palavra cabe nesta direção
              for (let i = 0; i < word.length; i++) {
                const checkRow = row + i * deltaRow
                const checkCol = col + i * deltaCol

                if (
                  checkRow < 0 ||
                  checkRow >= gameBoard.board.length ||
                  checkCol < 0 ||
                  checkCol >= gameBoard.board[0].length ||
                  gameBoard.board[checkRow][checkCol].letter !== word[i]
                ) {
                  isValidWord = false
                  break
                }

                currentPositions.push({ row: checkRow, column: checkCol })
              }

              // Se encontrou uma palavra válida, marcar todas as posições
              if (isValidWord) {
                currentPositions.forEach(pos => {
                  positions.add(`${pos.row}-${pos.column}`)
                })
              }
            })
          }
        }
      }
    })

    // Converter de volta para array de posições
    return Array.from(positions).map(pos => {
      const [row, col] = pos.split('-').map(Number)
      return { row, column: col }
    })
  }, [gameBoard, gameState.foundWords])

  // Calcula estatísticas do jogo
  const gameStats = useMemo(() => {
    return WordHuntService.calculateGameResult(gameBoard, gameState.foundWords)
  }, [gameBoard, gameState.foundWords])

  // Verifica se o jogo está completo
  const isComplete = useMemo(() => {
    return WordHuntService.isGameComplete(gameBoard, gameState.foundWords)
  }, [gameBoard, gameState.foundWords])

  /**
   * Verifica se uma posição já foi encontrada (parte de palavra já descoberta)
   */
  const isPositionFound = useCallback(
    (position: GamePosition): boolean => {
      return foundLetterPositions.some(
        foundPos =>
          foundPos.row === position.row && foundPos.column === position.column
      )
    },
    [foundLetterPositions]
  )

  /**
   * Inicia a seleção de uma palavra
   */
  const startSelection = useCallback(
    (position: GamePosition) => {
      // Impedir seleção se a letra já foi encontrada
      // if (isPositionFound(position)) {
      //   return
      // }

      setGameState(prev => ({
        ...prev,
        selectedPositions: [position],
        currentWord: gameBoard.board[position.row][position.column].letter,
        isSelecting: true
      }))
    },
    [gameBoard, isPositionFound]
  )

  /**
   * Constrói uma palavra a partir das posições selecionadas
   */
  const buildWordFromPositions = useCallback(
    (positions: GamePosition[]): string => {
      return positions
        .map(pos => gameBoard.board[pos.row][pos.column].letter)
        .join('')
    },
    [gameBoard]
  )

  /**
   * Adiciona uma posição à seleção atual
   */
  const addToSelection = useCallback(
    (position: GamePosition) => {
      setGameState(prev => {
        const lastPos =
          prev.selectedPositions[prev.selectedPositions.length - 1]

        // Verifica se a posição é adjacente
        if (!WordHuntService.isAdjacentPosition(position, lastPos)) {
          return prev
        }

        // Verifica se a posição já foi encontrada (não pode ser selecionada)
        // if (isPositionFound(position)) {
        //   return prev
        // }

        // Verifica se a posição já foi selecionada
        const isAlreadySelected = prev.selectedPositions.some(
          pos => pos.row === position.row && pos.column === position.column
        )

        if (isAlreadySelected) {
          // Remove posições até voltar à posição clicada (funcionalidade de voltar atrás)
          const clickIndex = prev.selectedPositions.findIndex(
            pos => pos.row === position.row && pos.column === position.column
          )

          const newPositions = prev.selectedPositions.slice(0, clickIndex + 1)
          const newWord = buildWordFromPositions(newPositions)

          return {
            ...prev,
            selectedPositions: newPositions,
            currentWord: newWord
          }
        }

        // Adiciona nova posição
        const newPositions = [...prev.selectedPositions, position]
        const newWord = buildWordFromPositions(newPositions)

        return {
          ...prev,
          selectedPositions: newPositions,
          currentWord: newWord
        }
      })
    },
    [buildWordFromPositions, isPositionFound]
  )

  /**
   * Finaliza a seleção e valida a palavra
   */
  const finishSelection = useCallback(() => {
    if (!gameState.isSelecting || gameState.selectedPositions.length === 0) {
      return
    }

    // use o min da config ou 3 como padrão
    const minLen = Math.max(config.minWordLength ?? 3, 2)

    // se for menor que o mínimo, não valida e não mostra erro
    if (gameState.selectedPositions.length < minLen) {
      setGameState(prev => ({
        ...prev,
        currentWord: '',
        selectedPositions: [],
        isSelecting: false
      }))
      return
    }

    const validation = WordHuntService.validateWord(
      gameBoard,
      gameState.selectedPositions,
      gameState.foundWords
    )

    if (validation.isValid && validation.word) {
      setSelectionError(false)
      setSelectionSuccess(true)

      setGameState(prev => ({
        ...prev,
        isSelecting: false
      }))

      setTimeout(() => {
        setSelectionSuccess(false)
        setGameState(prev => ({
          ...prev,
          foundWords: [...prev.foundWords, validation.word!],
          currentWord: '',
          selectedPositions: []
        }))
      }, 600)
    } else {
      // mantém o fluxo de erro apenas para seleções >= minLen
      setSelectionError(true)
      setGameState(prev => ({
        ...prev,
        currentWord: validation.reason || '',
        isSelecting: false
      }))

      setTimeout(() => {
        setSelectionError(false)
        setGameState(prev => ({
          ...prev,
          selectedPositions: []
        }))
      }, 800)

      actions.onError()
    }
  }, [config.minWordLength, gameState, gameBoard, actions])

  /**
   * Cancela a seleção atual
   */
  const cancelSelection = useCallback(() => {
    setGameState(prev => ({
      ...prev,
      currentWord: '',
      selectedPositions: [],
      isSelecting: false
    }))
  }, [])

  /**
   * Manipula clique em uma posição do tabuleiro
   */
  const handleSquareClick = useCallback(
    (position: GamePosition) => {
      if (gameState.isSelecting) {
        // Se já está selecionando, adiciona à seleção atual
        addToSelection(position)
      } else {
        // Inicia nova seleção
        startSelection(position)
      }
    },
    [gameState.isSelecting, addToSelection, startSelection]
  )

  /**
   * Manipula entrada do mouse em uma posição (drag)
   */
  const handleSquareEnter = useCallback(
    (position: GamePosition) => {
      if (gameState.isSelecting) {
        addToSelection(position)
      }
    },
    [gameState.isSelecting, addToSelection]
  )

  // Handler para finalizar seleção (chamado pelo Board via Pointer Events)
  const handleSelectionEnd = useCallback(() => {
    finishSelection()
  }, [finishSelection])

  // Efeito para detectar quando o jogo é completado
  useEffect(() => {
    if (isComplete) {
      setTimeout(actions.onComplete, 500) // Pequeno delay para feedback visual
    }
  }, [isComplete])

  // Função para resetar o jogo completamente
  const resetGame = useCallback(() => {
    clearSavedState()
    setGameState({
      currentWord: '',
      selectedPositions: [],
      foundWords: [],
      isSelecting: false
    })
    // O tabuleiro será regerado automaticamente no próximo render
  }, [clearSavedState])

  return {
    // Estado
    gameState,
    gameBoard,
    gameStats,
    isComplete,
    foundLetterPositions,
    selectionError,
    selectionSuccess,

    // Ações
    handleSquareClick,
    handleSquareEnter,
    handleSelectionEnd,
    cancelSelection,
    resetGame,

    // Utilitários
    isPositionActive: (position: GamePosition) =>
      gameState.selectedPositions.some(
        pos => pos.row === position.row && pos.column === position.column
      ),

    isPositionFound
  }
}
